import { cs } from '../../lib/currency';
import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { GraduationCap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

/**
 * Convert an approved-trial parent/child into a real enrolled
 * parent + player + enrollment.
 *
 * Pre-fills every field from the trial; admin can edit anything before save.
 * On submit, the backend de-dupes the parent by email and either reuses or
 * creates the player, then creates a `current` enrollment with the chosen
 * program / class / fee / frequency.
 */
export default function TrialEnrollDialog({ open, onOpenChange, trial, programs = [], onSuccess }) {
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    parent_name: '',
    parent_email: '',
    parent_phone: '',
    child_name: '',
    date_of_birth: '',
    skill_level: 'beginner',
    program_id: '',
    class_id: '',
    monthly_fee: '',
    payment_frequency: 'monthly',
    start_date: new Date().toISOString().slice(0, 10),
    // Default to TRUE so mid-month conversions automatically bill only
    // the remaining sessions. Admin can uncheck for full-month bills.
    prorate_first_month: true,
    notes: '',
  });

  // Prefill from the trial when the dialog opens.
  useEffect(() => {
    if (!open || !trial) return;
    setForm((f) => ({
      ...f,
      parent_name: trial.parent_name || '',
      parent_email: trial.parent_email || '',
      parent_phone: trial.parent_phone || '',
      child_name: trial.child_name || '',
      date_of_birth: trial.date_of_birth || trial.child_dob || '',
      skill_level: trial.skill_level || 'beginner',
      // If trial already had an assigned class, pre-pick its program too.
      program_id: '',
      class_id: trial.assigned_class_id || '',
      monthly_fee: '',
      payment_frequency: 'monthly',
      start_date: new Date().toISOString().slice(0, 10),
      prorate_first_month: true,
      notes: '',
    }));
  }, [open, trial]);

  // When program changes, fetch its classes and clear the chosen class.
  useEffect(() => {
    if (!form.program_id) { setClasses([]); return; }
    let cancelled = false;
    setClassesLoading(true);
    api.get(`/classes?program_id=${form.program_id}`)
      .then((r) => { if (!cancelled) setClasses(r.data || []); })
      .catch(() => { if (!cancelled) setClasses([]); })
      .finally(() => { if (!cancelled) setClassesLoading(false); });
    return () => { cancelled = true; };
  }, [form.program_id]);

  // If we prefilled an assigned_class_id from the trial, look up its program.
  useEffect(() => {
    if (!open || !trial?.assigned_class_id || form.program_id) return;
    api.get(`/classes/${trial.assigned_class_id}`)
      .then((r) => {
        const pid = r.data?.program_id;
        if (pid) setForm((f) => ({ ...f, program_id: pid, class_id: trial.assigned_class_id }));
      })
      .catch(() => {});
  }, [open, trial, form.program_id]);

  const selectedProgram = useMemo(
    () => programs.find((p) => p.id === form.program_id),
    [programs, form.program_id],
  );

  const update = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  const submit = async () => {
    // Validate
    if (!form.parent_name.trim() || !form.parent_email.trim()) {
      toast.error('Parent name and email are required');
      return;
    }
    if (!form.child_name.trim()) {
      toast.error('Child name is required');
      return;
    }
    if (!form.program_id || !form.class_id) {
      toast.error('Pick a program and class');
      return;
    }
    const feeNum = Number(form.monthly_fee);
    if (Number.isNaN(feeNum) || feeNum < 0) {
      toast.error('Monthly fee must be a non-negative number');
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post(`/trial-requests/${trial.id}/enroll-player`, {
        parent_name: form.parent_name.trim(),
        parent_email: form.parent_email.trim(),
        parent_phone: form.parent_phone.trim() || null,
        child_name: form.child_name.trim(),
        date_of_birth: form.date_of_birth || null,
        skill_level: form.skill_level || null,
        program_id: form.program_id,
        class_id: form.class_id,
        monthly_fee: feeNum,
        payment_frequency: form.payment_frequency,
        start_date: form.start_date || null,
        prorate_first_month: !!form.prorate_first_month,
        notes: form.notes.trim() || null,
      });
      const created = [];
      if (data.parent_created) created.push('parent');
      if (data.player_created) created.push('player');
      const tail = created.length ? ` (created ${created.join(' + ')})` : '';
      // Use the proration info the backend now returns to give the
      // admin specific feedback. Falls back to the previous message
      // when proration didn't apply (start of month, full-month bill).
      const msg = data.is_prorated
        ? `${data.message} — Prorated: ${cs()}${Number(data.prorated_amount).toFixed(2)} ` +
          `(${data.proration_sessions}/${data.total_sessions} sessions). ` +
          `Next billing: ${data.next_billing_date}`
        : data.message;
      toast.success(`${msg}${tail}`);
      onOpenChange(false);
      onSuccess && onSuccess(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enroll');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="trial-enroll-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-emerald-600" />
            Enroll Player from Trial
          </DialogTitle>
          <DialogDescription>
            Convert this approved trial into a real parent + player + active enrollment. Existing parent emails are de-duplicated automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Parent block */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-1">Parent</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="enroll-parent-name">Name *</Label>
                <Input id="enroll-parent-name" value={form.parent_name} onChange={(e) => update('parent_name', e.target.value)} data-testid="enroll-parent-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enroll-parent-email">Email *</Label>
                <Input id="enroll-parent-email" type="email" value={form.parent_email} onChange={(e) => update('parent_email', e.target.value)} data-testid="enroll-parent-email" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="enroll-parent-phone">Phone</Label>
                <Input id="enroll-parent-phone" value={form.parent_phone} onChange={(e) => update('parent_phone', e.target.value)} data-testid="enroll-parent-phone" />
              </div>
            </div>
          </section>

          {/* Child block */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-1">Child</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="enroll-child-name">Name *</Label>
                <Input id="enroll-child-name" value={form.child_name} onChange={(e) => update('child_name', e.target.value)} data-testid="enroll-child-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enroll-child-dob">Date of Birth</Label>
                <Input id="enroll-child-dob" type="date" value={form.date_of_birth} onChange={(e) => update('date_of_birth', e.target.value)} data-testid="enroll-child-dob" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enroll-skill">Skill Level</Label>
                <Select value={form.skill_level} onValueChange={(v) => update('skill_level', v)}>
                  <SelectTrigger id="enroll-skill" data-testid="enroll-skill"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>

          {/* Enrollment block */}
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-700 border-b pb-1">Enrollment</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="enroll-program">Program *</Label>
                <Select value={form.program_id} onValueChange={(v) => { update('program_id', v); update('class_id', ''); }}>
                  <SelectTrigger id="enroll-program" data-testid="enroll-program"><SelectValue placeholder="Choose program" /></SelectTrigger>
                  <SelectContent>
                    {programs.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="enroll-class">Class *</Label>
                <Select value={form.class_id} onValueChange={(v) => update('class_id', v)} disabled={!form.program_id || classesLoading}>
                  <SelectTrigger id="enroll-class" data-testid="enroll-class">
                    <SelectValue placeholder={classesLoading ? 'Loading…' : 'Choose class'} />
                  </SelectTrigger>
                  <SelectContent>
                    {classes.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                    {!classes.length && !classesLoading && (
                      <SelectItem value="__none__" disabled>No classes in this program</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="enroll-fee">Monthly Fee ($) *</Label>
                <Input
                  id="enroll-fee"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.monthly_fee}
                  onChange={(e) => update('monthly_fee', e.target.value)}
                  placeholder={selectedProgram?.monthly_fee ? `Suggested: ${cs()}${selectedProgram.monthly_fee}` : 'e.g. 250.00'}
                  data-testid="enroll-fee"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="enroll-frequency">Payment Frequency *</Label>
                <Select value={form.payment_frequency} onValueChange={(v) => update('payment_frequency', v)}>
                  <SelectTrigger id="enroll-frequency" data-testid="enroll-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly (3 months)</SelectItem>
                    <SelectItem value="biannual">Bi-Annual (6 months)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="enroll-start">Start Date</Label>
                <Input id="enroll-start" type="date" value={form.start_date} onChange={(e) => update('start_date', e.target.value)} data-testid="enroll-start" />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <label
                  className="flex items-start gap-2 p-3 rounded-md border bg-amber-50 border-amber-200 cursor-pointer"
                  data-testid="enroll-prorate-block"
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 text-emerald-600 rounded"
                    checked={!!form.prorate_first_month}
                    onChange={(e) => update('prorate_first_month', e.target.checked)}
                    data-testid="enroll-prorate-checkbox"
                  />
                  <span className="text-sm text-amber-900">
                    <span className="font-medium">Pro-rate first month</span>
                    <span className="block text-xs text-amber-700 mt-0.5">
                      When the start date is mid-month, charge only the remaining sessions for this month.
                      Recurring billing then resumes on the 1st of next month.
                    </span>
                  </span>
                </label>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="enroll-notes">Internal Notes</Label>
                <Textarea id="enroll-notes" rows={2} value={form.notes} onChange={(e) => update('notes', e.target.value)} data-testid="enroll-notes" />
              </div>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="enroll-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="enroll-submit">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Enroll Player
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
